/**
 * DTA-659 — what counts as a real form submission id.
 *
 * Submission ids are MongoDB ObjectIds: 24 hex characters. Nothing else is one,
 * and in particular `0` is not.
 *
 * `API.Post.setFormSubmissionId(feeData.formSubmissionId ?? 0, ...)` used to run
 * on every add-to-cart. The `?? 0` was a filler for a parameter wrongly typed
 * `number` (submission ids are strings), and it wrote the literal "0" into
 * ww_student_assigned_fees.fldFormSubmissionId 7,541 times.
 *
 * A "0" is strictly worse than a null. It severs the fee from any real
 * submission while claiming one exists, so:
 *   - the checkout gate accepted it (it only tested for null), letting
 *     form-required fees be paid with no form ever collected; and
 *   - the "Submit Form" button disappeared (that test was `=== null` too), so
 *     the family had no way to put it right.
 *
 * Use `isFormSubmissionId` for every "does this have a form submission?" test,
 * so a junk value reads as absent and the form can still be filled in.
 */
const OBJECT_ID = /^[0-9a-fA-F]{24}$/

export const isFormSubmissionId = (value: unknown): value is string =>
  typeof value === 'string' && OBJECT_ID.test(value.trim())

/** The value when it is a real submission id, otherwise null. */
export const toFormSubmissionId = (value: unknown): string | null =>
  isFormSubmissionId(value) ? value.trim() : null
